import { Fragment } from 'react';

import { cn } from '@ps/ui/utils';

interface Props {
  content: string;
  className?: string;
}

// `http(s)://` URLs only — keep the auto-linker tight to avoid
// flagging incidental dot-words. PopshopRN's `withLinks` plugin
// promotes URLs to proper `link` elements at type-time so Slate-DOM
// messages don't hit this path; plain-text messages from legacy
// clients are the main reason this exists.
const URL_REGEX = /(https?:\/\/\S+)/g;

/**
 * Render a plain-text message — preserves newlines via
 * `whitespace-pre-wrap` and turns http(s) URLs into clickable links.
 * No formatting marks; no markdown parsing.
 */
export function PlainTextMessageRenderer({ content, className }: Props) {
  return (
    <p
      className={cn(
        'whitespace-pre-wrap break-words text-sm leading-snug',
        className,
      )}
    >
      {splitWithLinks(content)}
    </p>
  );
}

function splitWithLinks(content: string) {
  const parts = content.split(URL_REGEX);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      // Trailing punctuation (".", ",", ")") commonly attaches to a
      // URL when the user types `…check it!` after a link. We don't
      // try to be clever — Slate-DOM is the canonical authoring
      // format, plain-text is fallback. Greedy capture is fine.
      return (
        <a
          key={i}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-text hover:text-brand-hover underline"
          onClick={event => event.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}
